/**
 * API Client
 * 統一的 API 請求處理
 * 提供認證、錯誤處理、請求攔截等功能
 */

import axios from 'axios';

// API Base URL - 自動添加 /api 後綴
const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

/**
 * 創建 Axios 實例
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds for AI endpoints
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * 請求攔截器
 * 自動添加認證 token
 */
import { getAuth } from 'firebase/auth';

/**
 * 請求攔截器
 * 自動添加認證 token
 */
apiClient.interceptors.request.use(
  async (config) => {
    const auth = getAuth();
    if (auth.currentUser) {
      try {
        // Force refresh if needed, ensures token is valid
        const token = await auth.currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${await window.__getUserIdToken()}`;
        // Update localStorage to keep it relatively fresh for other consumers
        localStorage.setItem('token', token);
      } catch (error) {
        console.error('Error fetching fresh token:', error);
      }
    } else {
      // Fallback for when auth state might not be synced yet but token exists
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${await window.__getUserIdToken()}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 回應攔截器
 * 統一處理錯誤和認證失效
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 處理認證失效
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // window.location.href = '/login';
    }

    // 處理網路錯誤
    if (!error.response) {
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        type: 'NETWORK_ERROR'
      });
    }

    // 返回格式化的錯誤
    return Promise.reject({
      message: error.response.data?.error?.message || error.response.data?.error || 'An error occurred',
      status: error.response.status,
      type: error.response.data?.error?.type || 'API_ERROR',
      details: error.response.data?.error?.details
    });
  }
);

/**
 * 通用 API 請求方法
 */
export const request = async (method, url, data = null, config = {}) => {
  try {
    const response = await apiClient({
      method,
      url,
      data,
      ...config
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * GET 請求
 */
export const get = (url, config = {}) => {
  return request('GET', url, null, config);
};

/**
 * POST 請求
 */
export const post = (url, data, config = {}) => {
  return request('POST', url, data, config);
};

/**
 * PUT 請求
 */
export const put = (url, data, config = {}) => {
  return request('PUT', url, data, config);
};

/**
 * DELETE 請求
 */
export const del = (url, config = {}) => {
  return request('DELETE', url, null, config);
};

/**
 * PATCH 請求
 */
export const patch = (url, data, config = {}) => {
  return request('PATCH', url, data, config);
};

/**
 * 文件上傳
 */
export const upload = (url, file, onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);

  return request('POST', url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  });
};

export default apiClient;
